/**
 * Investor Cash Flow Projection Page
 * 
 * Shows monthly actual vs projected cash flows with cumulative charts.
 * Uses recharts for interactive visualizations.
 */
import { useInvestorAuth } from "@/hooks/useInvestorAuth";
import InvestorLayout from "@/components/InvestorLayout";
import { trpc } from "@/lib/trpc";
import {
  AreaChart,
  Area,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import { TrendingUp, DollarSign, BarChart3, ArrowUpRight } from "lucide-react";

const BRAND = {
  bg: "#080d16",
  card: "#0e1623",
  border: "#1a2540",
  accent: "#3b82f6",
  accentLight: "rgba(59,130,246,0.1)",
  gold: "#f59e0b",
  goldLight: "rgba(245,158,11,0.1)",
  green: "#10b981",
  greenLight: "rgba(16,185,129,0.1)",
  text: "#f1f5f9",
  textMuted: "#94a3b8",
  textDim: "#64748b",
  chartGrid: "#1a2540",
  chartText: "#64748b",
};

function formatCurrency(value: number): string {
  if (value >= 1000000) return `$${(value / 1000000).toFixed(1)}M`;
  if (value >= 1000) return `$${(value / 1000).toFixed(0)}K`;
  return `$${value.toLocaleString()}`;
}

function formatMonthLabel(month: string): string {
  const [year, m] = month.split("-");
  const date = new Date(parseInt(year), parseInt(m) - 1, 1);
  return date.toLocaleDateString("en-US", { month: "short", year: "2-digit" });
}

const CustomTooltip = ({ active, payload, label }: any) => {
  if (!active || !payload?.length) return null;
  return (
    <div
      style={{
        background: "#0e1623",
        border: "1px solid #1a2540",
        borderRadius: 10,
        padding: "12px 16px",
        boxShadow: "0 8px 24px rgba(0,0,0,0.4)",
      }}
    >
      <div style={{ color: "#94a3b8", fontSize: 12, marginBottom: 8 }}>{formatMonthLabel(label)}</div>
      {payload.map((p: any) => (
        <div
          key={p.name}
          style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}
        >
          <div
            style={{
              width: 8,
              height: 8,
              borderRadius: "50%",
              background: p.color,
            }}
          />
          <span style={{ color: "#94a3b8", fontSize: 12 }}>{p.name}:</span>
          <span style={{ color: "#f1f5f9", fontSize: 12, fontWeight: 600 }}>
            {formatCurrency(p.value)}
          </span>
        </div>
      ))}
    </div>
  );
};

export default function InvestorCashFlow() {
  const { user, loading: authLoading } = useInvestorAuth({ redirectOnUnauthenticated: true });

  const cashFlowQuery = trpc.investor.getCashFlowProjections.useQuery(
    { investorId: user?.id ?? 0 },
    { enabled: !!user?.id }
  );

  const data = cashFlowQuery.data || [];

  // Format for charts
  const chartData = data.map((d: any) => ({
    month: d.month,
    label: formatMonthLabel(d.month),
    actual: d.actual,
    projected: d.projected,
    cumulativeActual: d.cumulativeActual,
    cumulativeProjected: d.cumulativeProjected,
  }));

  const totalActual = data.reduce((sum: number, d: any) => sum + (d.actual || 0), 0);
  const totalProjected = data.reduce((sum: number, d: any) => sum + (d.projected || 0), 0);
  const collectionRate = totalProjected > 0 ? (totalActual / totalProjected) * 100 : 0;

  return (
    <InvestorLayout>
      <div style={{ padding: "32px 36px", maxWidth: 1200 }}>
        {/* Header */}
        <div style={{ marginBottom: 28 }}>
          <h1 style={{ color: BRAND.text, fontSize: 24, fontWeight: 700, margin: "0 0 6px 0" }}>
            Cash Flow Projections
          </h1>
          <p style={{ color: BRAND.textMuted, fontSize: 14, margin: 0 }}>
            Monthly actual collections vs projected payment schedule.
          </p>
        </div>

        {/* Summary Cards */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
            gap: 14,
            marginBottom: 28,
          }}
        >
          {[
            {
              label: "Total Collected",
              value: formatCurrency(totalActual),
              icon: DollarSign,
              color: BRAND.green,
              colorLight: BRAND.greenLight,
            },
            {
              label: "Total Projected",
              value: formatCurrency(totalProjected),
              icon: BarChart3,
              color: BRAND.accent,
              colorLight: BRAND.accentLight,
            },
            {
              label: "Collection Rate",
              value: `${collectionRate.toFixed(1)}%`,
              icon: TrendingUp,
              color: BRAND.gold,
              colorLight: BRAND.goldLight,
            },
          ].map((item) => (
            <div
              key={item.label}
              style={{
                background: BRAND.card,
                border: `1px solid ${BRAND.border}`,
                borderRadius: 12,
                padding: "16px 18px",
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
                <div
                  style={{
                    width: 30,
                    height: 30,
                    borderRadius: 8,
                    background: item.colorLight,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <item.icon size={14} color={item.color} />
                </div>
                <span style={{ color: BRAND.textMuted, fontSize: 12 }}>{item.label}</span>
              </div>
              <div style={{ color: BRAND.text, fontSize: 22, fontWeight: 700 }}>{item.value}</div>
            </div>
          ))}
        </div>

        {/* Monthly Bar Chart: Actual vs Projected */}
        <div
          style={{
            background: BRAND.card,
            border: `1px solid ${BRAND.border}`,
            borderRadius: 14,
            padding: "24px",
            marginBottom: 20,
          }}
        >
          <h2 style={{ color: BRAND.text, fontSize: 15, fontWeight: 700, margin: "0 0 20px 0" }}>
            Monthly Cash Flow — Actual vs Projected
          </h2>
          {cashFlowQuery.isLoading ? (
            <div style={{ height: 280, display: "flex", alignItems: "center", justifyContent: "center" }}>
              <span style={{ color: BRAND.textMuted, fontSize: 14 }}>Loading chart data...</span>
            </div>
          ) : chartData.length === 0 ? (
            <div style={{ height: 280, display: "flex", alignItems: "center", justifyContent: "center" }}>
              <span style={{ color: BRAND.textDim, fontSize: 14 }}>
                No cash flow data available yet. Payments will appear here once recorded.
              </span>
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={chartData} margin={{ top: 4, right: 4, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={BRAND.chartGrid} vertical={false} />
                <XAxis
                  dataKey="label"
                  tick={{ fill: BRAND.chartText, fontSize: 11 }}
                  axisLine={false}
                  tickLine={false}
                />
                <YAxis
                  tickFormatter={formatCurrency}
                  tick={{ fill: BRAND.chartText, fontSize: 11 }}
                  axisLine={false}
                  tickLine={false}
                  width={60}
                />
                <Tooltip content={<CustomTooltip />} />
                <Legend
                  wrapperStyle={{ color: BRAND.textMuted, fontSize: 12, paddingTop: 12 }}
                />
                <Bar dataKey="projected" name="Projected" fill={BRAND.accent} opacity={0.5} radius={[4, 4, 0, 0]} />
                <Bar dataKey="actual" name="Actual" fill={BRAND.green} radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>

        {/* Cumulative Area Chart */}
        <div
          style={{
            background: BRAND.card,
            border: `1px solid ${BRAND.border}`,
            borderRadius: 14,
            padding: "24px",
          }}
        >
          <h2 style={{ color: BRAND.text, fontSize: 15, fontWeight: 700, margin: "0 0 20px 0" }}>
            Cumulative Collections
          </h2>
          {cashFlowQuery.isLoading ? (
            <div style={{ height: 280, display: "flex", alignItems: "center", justifyContent: "center" }}>
              <span style={{ color: BRAND.textMuted, fontSize: 14 }}>Loading chart data...</span>
            </div>
          ) : chartData.length === 0 ? (
            <div style={{ height: 280, display: "flex", alignItems: "center", justifyContent: "center" }}>
              <span style={{ color: BRAND.textDim, fontSize: 14 }}>
                No data to display yet.
              </span>
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={280}>
              <AreaChart data={chartData} margin={{ top: 4, right: 4, left: 0, bottom: 0 }}>
                <defs>
                  <linearGradient id="gradActual" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor={BRAND.green} stopOpacity={0.25} />
                    <stop offset="95%" stopColor={BRAND.green} stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="gradProjected" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor={BRAND.accent} stopOpacity={0.2} />
                    <stop offset="95%" stopColor={BRAND.accent} stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke={BRAND.chartGrid} vertical={false} />
                <XAxis
                  dataKey="label"
                  tick={{ fill: BRAND.chartText, fontSize: 11 }}
                  axisLine={false}
                  tickLine={false}
                />
                <YAxis
                  tickFormatter={formatCurrency}
                  tick={{ fill: BRAND.chartText, fontSize: 11 }}
                  axisLine={false}
                  tickLine={false}
                  width={60}
                />
                <Tooltip content={<CustomTooltip />} />
                <Legend
                  wrapperStyle={{ color: BRAND.textMuted, fontSize: 12, paddingTop: 12 }}
                />
                <Area
                  type="monotone"
                  dataKey="cumulativeProjected"
                  name="Cumulative Projected"
                  stroke={BRAND.accent}
                  strokeWidth={2}
                  fill="url(#gradProjected)"
                  strokeDasharray="5 3"
                />
                <Area
                  type="monotone"
                  dataKey="cumulativeActual"
                  name="Cumulative Actual"
                  stroke={BRAND.green}
                  strokeWidth={2.5}
                  fill="url(#gradActual)"
                />
              </AreaChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>
    </InvestorLayout>
  );
}
